import {Category} from '@wix/ambassador-events-v1-category/types'
import {Event as Event_V3} from '@wix/ambassador-events-v3-event/types'
import {IWixStyleParams, getStylesValues} from '@wix/tpa-settings'
import {
  EVENTS_APP_ID,
  EVENT_FILTER_TYPE,
  ExperimentNames,
  FEATURED_EVENT_DISPLAY_STRATEGY,
  LIST_DISPLAY_STRATEGY,
  WIDGET_TYPE,
  isRtlLanguage,
  sortEvents,
  sortEventsV3,
} from '@wix/wix-events-commons-statics'
import {ControllerParams, CreateControllerFn} from '@wix/yoshi-flow-editor'
import {PreloadedState, Store, bindActionCreators} from 'redux'
import {setBaseEnvironment, setFormFactor} from '../../commons/actions/environment'
import {watchInstance} from '../../commons/actions/instance'
import {createUouBiMiddlewareWithBiParams} from '../../commons/bi/bi'
import {EMPTY_CATEGORY_FILTER} from '../../commons/constants/settings'
import {UserRole} from '../../commons/enums'
import {createErrorLoggerMiddleware} from '../../commons/middlewares/error-logger'
import {getInitialCategoryId, getNullableCategoryId, isCategoryMenuMode} from '../../commons/selectors/categories'
import {isResponsiveEditor} from '../../commons/selectors/environment'
import {isEventsInMembersInstalled} from '../../commons/selectors/installed-apps'
import {decodeInstance} from '../../commons/selectors/instance'
import {getInstalledAppsState} from '../../commons/services/installed-apps'
import {getMultilingualInitialState} from '../../commons/services/multilingual'
import {getQueryParams} from '../../commons/services/navigation'
import {createReduxStore} from '../../commons/utils/store'
import {getLanguage, getPageInfo, isEditor, isEditor3} from '../../commons/utils/wix-code-api'
import settingsParams from './settingsParams'
import {CommonWidgetDataParams} from './shared-api'
import stylesParams, {WidgetComponentSettings, getNumbersAndBooleans} from './stylesParams'
import {unpackWidgetData} from './utils/widget-data'
import {
  addCalendarMonth,
  changeCalendarCategory,
  closeMonthlyCalendarEvent,
  closeMonthlyCalendarPopup,
  loadCalendar,
  openMonthlyCalendarEvent,
  openMonthlyCalendarPopup,
  resetCalendar,
  subCalendarMonth,
} from './Widget/actions/calendar-layout'
import {selectCategory} from './Widget/actions/categories'
import {updateStyles} from './Widget/actions/component'
import {shareEvent} from './Widget/actions/event'
import {loadEventsPage} from './Widget/actions/events'
import {closeListLayoutItems, openListLayoutItem, widgetLoaded} from './Widget/actions/layout'
import {loadMembersForEvents, memberLoggedIn, openMembersModal} from './Widget/actions/members'
import {navigateToPage, onLinkNavigatedToPage} from './Widget/actions/navigate-to-page'
import {SettingsUpdateAction, updateSettings} from './Widget/actions/sdk'
import {handleViewState} from './Widget/actions/settings-panel'
import {setVeloCategoryFilter} from './Widget/actions/velo-overrides'
import * as eventsUou from './Widget/bi/uou-bi-events-map'
import {AppProps} from './Widget/components/app/interfaces'
import {datesMiddleware} from './Widget/middlewares/date'
import reducers from './Widget/reducers'
import {getDefaultReferenceDay} from './Widget/selectors/calendar-layout'
import {getCategoryIdFilter, getInitialFetchCategoryId} from './Widget/selectors/categories'
import {isCalendarLayout, isMobileCalendarLayout, isMobileDailyListLayout} from './Widget/selectors/list-settings'
import {getDynamicCategoryId} from './Widget/selectors/velo-overrides'
import {Actions, State, StoreExtraArgs} from './Widget/types/state'
import {userEventsLogger} from './Widget/user-events-logger'
import {Api} from './Widget/utils/api'

const createWidgetController: CreateControllerFn = async (controllerParams: ControllerParams) => {
  const serverApi = new Api(controllerParams)
  const store = await createStore({controller: controllerParams, serverApi})

  return {
    pageReady: async () => {
      return pageReady({controllerParams, store, serverApi})
    },
    exports: () => ({
      set categoryId(id: string) {
        store.dispatch(selectCategory(id))
      },
      get categoryId() {
        return getDynamicCategoryId(store.getState(), controllerParams.flowAPI)
      },
    }),
    updateAppSettings: async (_$w, action) => {
      if (store) {
        setTimeout(() => {
          store.dispatch(updateSettings(action as SettingsUpdateAction))
        }, 100)
      }
    },
    updateConfig: (_$w, newConfig) => {
      const {isMobile, isEditorX} = controllerParams.flowAPI.environment
      const mobileStyles = isMobile && isEditorX ? getEditor3MobileStyles(newConfig.allStyles) : {}

      store.dispatch(
        updateStyles({
          numbers: {
            ...newConfig.style.styleParams.numbers,
            ...mobileStyles,
          },
          booleans: newConfig.style.styleParams.booleans,
        }),
      )
    },
  }
}

interface PageReadyParams {
  controllerParams: ControllerParams
  store: Store<State>
  serverApi: Api
}

const pageReady = async ({store, controllerParams}: PageReadyParams) => {
  const {
    controllerConfig: controller,
    flowAPI: {
      environment: {isMobile, isEditorX},
    },
  } = controllerParams
  const language = getLanguage(controller.wixCodeApi)
  const mobileStyles = isMobile && isEditorX ? getEditor3MobileStyles(controller.config.allStyles) : undefined
  const featuredWidget =
    (mobileStyles?.widgetType ?? controller.config.style.styleParams.numbers.widgetType) === WIDGET_TYPE.FEATURED

  const [pageInfo, pageUrl] = await Promise.all([
    getPageInfo(controller.wixCodeApi),
    controller.wixCodeApi.site.getSectionUrl({
      sectionId: 'events',
      appDefinitionId: EVENTS_APP_ID,
    }),
  ])

  const state = store.getState()
  const props: Partial<AppProps> = {
    pageUrl,
    baseUrl: controller.wixCodeApi.location.baseUrl,
    queryParams: getQueryParams(controller.wixCodeApi),
    pageInfo,
    state,
    actions: exportedActions({store}),
    isRTL: isRtlLanguage(language),
    // @ts-expect-error
    fitToContentHeight: true,
    featuredWidget,
  }

  controller.setProps(props)
  subscribeToStateChanges(controllerParams, store)
}

interface CreateStoreParams {
  controller: ControllerParams
  serverApi: Api
}

const createStore = async ({controller, serverApi}: CreateStoreParams) => {
  const {
    controllerConfig: {wixCodeApi, platformAPIs, appParams, compId, config},
    flowAPI,
  } = controller

  let initialData: Awaited<ReturnType<typeof getInitialData>>
  const warmupDataKey = `widget${compId}`

  if (flowAPI.experiments.enabled(ExperimentNames.UseWarmupState) && flowAPI.environment.isSSR) {
    initialData = await getInitialData({serverApi, controller})
    wixCodeApi.window.warmupData.set(warmupDataKey, initialData)
  } else {
    initialData = wixCodeApi.window.warmupData.get(warmupDataKey) ?? (await getInitialData({serverApi, controller}))
  }

  const store = createReduxStore<State, StoreExtraArgs>({
    reducers,
    initialData,
    extraArguments: {
      serverApi,
      wixCodeApi,
      compId,
      flowAPI,
    },
    middleware: [
      createUouBiMiddlewareWithBiParams({wixCodeApi, flowAPI, platformAPIs, appParams, compId}, eventsUou),
      userEventsLogger({wixCodeApi}),
      datesMiddleware,
      createErrorLoggerMiddleware({errorMonitor: flowAPI.panoramaClient.errorMonitor}),
    ],
  })

  wixCodeApi.user.onLogin(async user => {
    if (user.loggedIn) {
      store.dispatch(memberLoggedIn())
    }
  })

  await store.dispatch(setBaseEnvironment())

  const {listShowMembers = true, showMembers = true} = config.style.styleParams.booleans
  const membersVisible =
    config.style.styleParams.numbers.widgetType === WIDGET_TYPE.SINGLE ? showMembers : listShowMembers
  const eventsInMembersInstalled = isEventsInMembersInstalled(store.getState())

  if (membersVisible && eventsInMembersInstalled) {
    store.dispatch(loadMembersForEvents())

    serverApi.onLogin(() => {
      store.dispatch(loadMembersForEvents())
    })
  }

  watchInstance(controller.controllerConfig, store.dispatch)

  return store
}

const subscribeToStateChanges = (controllerParams: ControllerParams, store: Store<State>) => {
  const onStateChange = () => {
    const state = store.getState()

    controllerParams.controllerConfig.setProps({state})
  }

  store.subscribe(onStateChange)
}

interface GetInitialDataParams {
  serverApi: Api
  controller: ControllerParams
}

const getInitialData = async ({serverApi, controller}: GetInitialDataParams): Promise<PreloadedState<State>> => {
  const {compId, language, locale, tz, viewMode} = serverApi
  const {
    flowAPI,
    controllerConfig: {
      wixCodeApi,
      appParams: {instance},
      config: controllerConfig,
    },
  } = controller
  const {isMobile, isEditorX} = flowAPI.environment

  const newClassicEvents = flowAPI.experiments.enabled(ExperimentNames.NewClassicEvents)

  const filterType = flowAPI.settings.get(settingsParams.filterType) as number
  const sortOrder = flowAPI.settings.get(settingsParams.sortOrder) as number
  const limit = flowAPI.settings.get(settingsParams.eventsPerPage) as number

  const listDisplayStrategy = flowAPI.settings.get(settingsParams.listDisplayStrategy) as LIST_DISPLAY_STRATEGY
  const listStatusFilter = flowAPI.settings.get(settingsParams.listStatusFilter) as number

  const featuredCategoryId = flowAPI.settings.get(settingsParams.featuredCategoryId) as string
  const featuredEventId = flowAPI.settings.get(settingsParams.featuredEventId) as string
  const featuredDisplayStrategy = flowAPI.settings.get(
    settingsParams.featuredEventDisplayStrategy,
  ) as FEATURED_EVENT_DISPLAY_STRATEGY

  const installedApps = await getInstalledAppsState(wixCodeApi)

  const decodedInstance = decodeInstance(instance)
  const responsive = isResponsiveEditor(controllerConfig)
  const editor = isEditor(wixCodeApi)
  const editor3 = isEditor3(wixCodeApi)
  const showcase = decodedInstance.siteIsTemplate || editor

  const {listShowMembers = true, showMembers = true} = controllerConfig.style.styleParams.booleans
  const {widgetType, recurringFilter} = controllerConfig.style.styleParams.numbers
  const mobileStyles = isMobile && isEditorX ? getEditor3MobileStyles(controllerConfig.allStyles) : undefined
  const featuredWidget = (mobileStyles?.widgetType ?? widgetType) === WIDGET_TYPE.FEATURED
  const singleWidget = widgetType === WIDGET_TYPE.SINGLE

  const commonArgs: CommonWidgetDataParams = {
    members: installedApps.eventsInMembersInstalled && (singleWidget ? showMembers : listShowMembers),
    paidPlans: installedApps.paidPlans,
    responsive,
    language,
    locale,
    tz,
    viewMode,
    newClassicEvents,
    editor3,
  }

  const categoryMenuMode = isCategoryMenuMode(flowAPI.settings.get)
  const categoryId = getCategoryIdFilter(flowAPI.settings.get)
  const fetchCategoryId = getInitialFetchCategoryId(flowAPI.settings.get)

  const serverlessWidgetDataEnabled = flowAPI.experiments.enabled(ExperimentNames.ServerlessWidgetData)

  const stylesValues = getStylesValues(
    {
      ...controllerConfig.style.styleParams,
      numbers: {
        ...controllerConfig.style.styleParams.numbers,
        ...mobileStyles,
      },
    },
    stylesParams,
    {
      isMobile: flowAPI.environment.isMobile,
      isRTL: flowAPI.environment.isRTL,
    },
  )

  const component = {
    id: compId,
    settings: getNumbersAndBooleans(stylesValues) as WidgetComponentSettings,
  }

  const calendarEvents = flowAPI.environment.isMobile
    ? isMobileDailyListLayout(component) || isMobileCalendarLayout(component)
    : isCalendarLayout(component)

  const widgetData = featuredWidget
    ? await serverApi.getFeaturedWidgetData({
        ...commonArgs,
        eventId: featuredEventId,
        categoryId: featuredCategoryId,
        manuallySelected: featuredDisplayStrategy === FEATURED_EVENT_DISPLAY_STRATEGY.CUSTOM,
        useServerless: serverlessWidgetDataEnabled,
      })
    : await serverApi.getWidgetData({
        ...commonArgs,
        compId,
        showcase,
        calendarEvents,
        recurringFilter,
        categoryId,
        fetchCategoryId,
        fetchAllCategories: editor,
        noCategoryIdFallback: categoryId === EMPTY_CATEGORY_FILTER || categoryId?.includes?.(EMPTY_CATEGORY_FILTER),
        filterType: listDisplayStrategy === LIST_DISPLAY_STRATEGY.CUSTOM ? EVENT_FILTER_TYPE.MANUAL : listStatusFilter,
        sortOrder: filterType === EVENT_FILTER_TYPE.MANUAL ? sortOrder : undefined,
        limit: editor ? 20 : limit,
        useServerless: serverlessWidgetDataEnabled,
      })

  const {
    events: widgetDataEvents,
    featuredEvent,
    members,
    membershipOffers,
    totalGuests,
    hasMoreEvents,
    categories,
    demoEvents,
    siteSettings,
    dates,
  } = unpackWidgetData(widgetData, serverlessWidgetDataEnabled, featuredWidget)

  const initialCategoryId = categoryMenuMode
    ? getInitialCategoryId(flowAPI.settings.get, categories as unknown as Category[])
    : null
  const initialVeloCategoryId = getNullableCategoryId(initialCategoryId)

  const events = featuredEvent ? [featuredEvent] : widgetDataEvents ?? []

  const sortedEvents =
    filterType === EVENT_FILTER_TYPE.MANUAL
      ? events
      : serverlessWidgetDataEnabled
      ? sortEventsV3(events as Event_V3[])
      : sortEvents(events as wix.events.Event[])

  return {
    events: {
      events: sortedEvents,
      hasMore: hasMoreEvents,
      moreLoading: false,
      filterType,
      latestRequestId: '',
    },
    siteSettings,
    demoEvents,
    multilingual: getMultilingualInitialState(wixCodeApi),
    component,
    memberLoggedIn: wixCodeApi.user.currentUser.loggedIn,
    owner: wixCodeApi.user.currentUser.role === UserRole.ADMIN,
    instance: {
      instance,
      ...decodedInstance,
    },
    calendarLayout: {
      ...(dates.calendar ?? {}),
      referenceDay: getDefaultReferenceDay(showcase, dates),
      referenceMonth: dates.calendar?.referenceDate,
      loadedPages: dates.calendar?.referenceDate ? [dates.calendar.referenceDate] : [],
    },
    categories: {
      categories: categories ?? [],
    },
    dynamicVeloOverrides: {
      categoryId: initialVeloCategoryId,
    },
    dates,
    installedApps,
    members: {members},
    membershipOffers: {membershipOffers},
    totalGuests: {totalGuests},
  } as PreloadedState<State>
}

const exportedActions = ({store}) => {
  const dispatchActions = {
    loadCalendar,
    changeCalendarCategory,
    addCalendarMonth,
    subCalendarMonth,
    resetCalendar,
    navigateToPage,
    onLinkNavigatedToPage,
    updateStyles,
    updateSettings,
    setBaseEnvironment,
    openListLayoutItem,
    closeListLayoutItems,
    openMonthlyCalendarPopup,
    closeMonthlyCalendarPopup,
    openMonthlyCalendarEvent,
    closeMonthlyCalendarEvent,
    widgetLoaded,
    openMembersModal,
    shareEvent,
    setFormFactor,
    loadEventsPage,
    selectCategory,
    setVeloCategoryFilter,
    handleViewState,
  }

  const actions: Actions = {
    ...bindActionCreators(dispatchActions, store.dispatch),
  }

  return actions
}

const getEditor3MobileStyles = (styles: IWixStyleParams[] | undefined): Record<string, number> => {
  const numbers = styles?.find(style => style.numbers.mobileWidgetLayoutNew !== undefined)?.numbers

  return numbers
    ? Object.fromEntries(
        (['mobileWidgetLayoutNew', 'widgetType'] as const)
          .filter(key => numbers[key] !== undefined)
          .map(key => [key, numbers[key]]),
      )
    : {}
}

export default createWidgetController
